import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Lock, Loader2, ArrowLeft, CheckCircle, Shield, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PaymentPagePayPal = () => {
  const [loading, setLoading] = useState(true);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadPaymentSettings();
  }, []);

  const loadPaymentSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/payment/public`);
      if (response.ok) {
        const data = await response.json();
        setPaymentSettings(data);
        // Auto-select first method if only one available
        if (data.payment_methods?.length === 1) {
          setSelectedMethod(data.payment_methods[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load payment settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = () => {
    if (!paymentSettings) return 'CA$2.99';
    const { price_amount, price_currency } = paymentSettings;
    const currencySymbols = {
      'CAD': 'CA$',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'INR': '₹',
      'AUD': 'A$',
      'PKR': 'Rs'
    };
    const symbol = currencySymbols[price_currency] || price_currency + ' ';
    return `${symbol}${price_amount}`;
  };

  const handlePayment = () => {
    if (!selectedMethod?.link) {
      toast({
        title: "Select Payment Method",
        description: "Please select a payment method to continue",
        variant: "destructive",
      });
      return;
    }

    // Store payment initiation for tracking
    sessionStorage.setItem('paymentInitiated', JSON.stringify({
      timestamp: new Date().toISOString(),
      method: selectedMethod.name,
      amount: paymentSettings.price_amount,
      currency: paymentSettings.price_currency
    }));

    // Build the payment URL
    let paymentUrl = selectedMethod.link;
    
    // For PayPal.me links, append amount
    if (paymentUrl.includes('paypal.me/') && !paymentUrl.includes('?')) {
      paymentUrl = `${paymentUrl}/${paymentSettings.price_amount}${paymentSettings.price_currency}`;
    }

    // Ensure URL has protocol
    if (!paymentUrl.startsWith('http')) {
      paymentUrl = 'https://' + paymentUrl;
    }

    // Open payment link
    window.open(paymentUrl, '_blank');
    
    // Show toast with instructions
    toast({
      title: "Complete Payment",
      description: "Complete your payment in the new window. Your premium will be activated within 24 hours after confirmation.",
    });
  };

  const getPaymentIcon = (iconName, color) => {
    const iconStyle = { color: color || '#6b7280' };
    
    const icons = {
      paypal: (
        <svg viewBox="0 0 24 24" className="w-6 h-6" fill={color || '#0070ba'}>
          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.65h6.916c2.077 0 3.573.405 4.44 1.204.855.79 1.188 1.942.989 3.423-.02.152-.044.3-.072.449l-.004.023c-.555 3.01-2.481 4.48-5.719 4.48H9.677a.77.77 0 0 0-.757.65l-.918 5.788a.641.641 0 0 1-.633.74h-.293v1.51z"/>
        </svg>
      ),
      stripe: (
        <svg viewBox="0 0 24 24" className="w-6 h-6" fill={color || '#635bff'}>
          <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
        </svg>
      ),
      square: (
        <svg viewBox="0 0 24 24" className="w-6 h-6" fill={color || '#006aff'}>
          <path d="M4.5 0A4.5 4.5 0 0 0 0 4.5v15A4.5 4.5 0 0 0 4.5 24h15a4.5 4.5 0 0 0 4.5-4.5v-15A4.5 4.5 0 0 0 19.5 0h-15zm4.125 6h6.75c1.17 0 2.125.955 2.125 2.125v6.75c0 1.17-.955 2.125-2.125 2.125h-6.75A2.126 2.126 0 0 1 6.5 14.875v-6.75C6.5 6.955 7.455 6 8.625 6z"/>
        </svg>
      ),
      default: <CreditCard className="w-6 h-6" style={iconStyle} />
    };
    
    return icons[iconName] || icons.default;
  };

  const features = [
    'Unlimited Profile Views',
    'Send Unlimited Messages',
    'Advanced Search Filters',
    'Verified Premium Badge',
    'Priority Customer Support',
    'Stand Out from Others'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-mint-500" />
      </div>
    );
  }

  // Check if payments are enabled
  const enabledMethods = paymentSettings?.payment_methods?.filter(m => m.enabled && m.link) || [];
  
  if (!paymentSettings?.payment_enabled || enabledMethods.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-md mx-auto px-4">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Payments Coming Soon</h2>
              <p className="text-gray-600 mb-6">
                We're setting up our payment system. Please check back soon!
              </p>
              <Button
                onClick={() => navigate('/pricing')}
                variant="outline"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Pricing
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="w-full px-4 max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/pricing')}
          className="flex items-center text-gray-600 hover:text-navy-500 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Pricing
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg">{paymentSettings.plan_name} Plan</h3>
                  <p className="text-sm text-gray-600">{paymentSettings.plan_duration || '1 Year'} Subscription</p>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-semibold mb-2">Features Included:</h4>
                  <ul className="space-y-2">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-start text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-navy-500">{formatPrice()}/{paymentSettings.plan_duration || '1 Year'}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Billed {paymentSettings.plan_duration === '1 Year' ? 'annually' : paymentSettings.plan_duration?.toLowerCase() || 'annually'} in {paymentSettings.price_currency}. Cancel anytime.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Methods */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center mb-6">
                <CreditCard className="w-6 h-6 text-navy-500 mr-2" />
                <h2 className="text-2xl font-bold">Choose Payment Method</h2>
              </div>

              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start">
                  <Lock className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Secure Checkout</p>
                    <p className="text-xs text-blue-700 mt-1">
                      All payments are processed securely. We never store your payment details.
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Method Selection */}
              <div className="space-y-3 mb-6">
                {enabledMethods.map((method) => (
                  <button
                    key={method.id}
                    onClick={() => setSelectedMethod(method)}
                    className={`w-full flex items-center gap-4 p-4 border-2 rounded-xl transition-all ${
                      selectedMethod?.id === method.id
                        ? 'border-mint-500 bg-mint-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: method.color + '15' }}
                    >
                      {getPaymentIcon(method.icon, method.color)}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-semibold text-gray-900">{method.name}</p>
                      {method.description && (
                        <p className="text-sm text-gray-500">{method.description}</p>
                      )}
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedMethod?.id === method.id
                        ? 'border-mint-500 bg-mint-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedMethod?.id === method.id && (
                        <div className="w-2 h-2 bg-white rounded-full" />
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* Currency Notice */}
              <div className="mb-6 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start">
                  <AlertCircle className="w-4 h-4 text-amber-600 mr-2 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    Payment will be processed in <strong>{paymentSettings.price_currency}</strong>. 
                    Your bank may apply currency conversion fees if you're paying from a different currency.
                  </p>
                </div>
              </div>

              <Button
                onClick={handlePayment}
                disabled={!selectedMethod}
                className="w-full bg-mint-500 hover:bg-mint-600 text-white py-6 text-lg disabled:opacity-50"
                data-testid="pay-now-btn"
              >
                {selectedMethod ? (
                  <>
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Pay {formatPrice()} with {selectedMethod.name}
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Select a Payment Method
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-gray-500 mt-4">
                By continuing, you agree to our Terms of Service and Privacy Policy.
                After payment, your premium will be activated within 24 hours.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center space-x-6 text-gray-400">
            <div className="flex items-center">
              <Lock className="w-4 h-4 mr-1" />
              <span className="text-xs">SSL Encrypted</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span className="text-xs">100% Secure</span>
            </div>
            <div className="flex items-center">
              <Shield className="w-4 h-4 mr-1" />
              <span className="text-xs">Buyer Protected</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPagePayPal;
